import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Renders visual breadcrumbs. Accepts items = [{label, to?}]. Last item is treated as current page.
 * Also exports buildBreadcrumbSchema() for JSON-LD injection via SEO's `schemas` prop.
 */
export default function Breadcrumbs({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-4" data-testid="breadcrumbs">
      <ol className="flex items-center gap-1.5 flex-wrap">
        {items.map((it, i) => {
          const isLast = i === items.length - 1;
          return (
            <li key={i} className="flex items-center gap-1.5">
              {i > 0 && <ChevronRight className="w-3 h-3 opacity-50" />}
              {it.to && !isLast ? (
                <Link to={it.to} className="hover:text-teal capitalize">{it.label}</Link>
              ) : (
                <span className="capitalize text-foreground/80" aria-current={isLast ? "page" : undefined}>{it.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * Build JSON-LD BreadcrumbList schema.
 * @param {Array<{label:string, to?:string}>} items
 * @param {string} origin — window.location.origin (auto-detected if omitted)
 */
export function buildBreadcrumbSchema(items, origin) {
  if (!items || items.length === 0) return null;
  const base = origin || (typeof window !== "undefined" ? window.location.origin : "");
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "name": it.label,
      ...(it.to ? { "item": `${base}${it.to}` } : {}),
    })),
  };
}
